import { useState, useEffect, useCallback } from 'react';

/**
 * Dashboard data-fetching hook.
 *
 * 1. Parallel fetch: /user, /portfolio, /agents/invitations/pending, /agents/observer-portfolios, /agents
 * 2. Partition portfolios into hlPortfolio (HYPERLIQUID mainnet) vs paperPortfolios
 * 3. Parallel fetch GET /portfolio/:id for each portfolio to get value/PnL/positions
 * 4. Store detail results in a Map<id, detailData>
 */
export function useDashboardPortfolios(config, jwtToken) {
  const [userData, setUserData] = useState(null);
  const [agents, setAgents] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [observerPortfolios, setObserverPortfolios] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [portfolioDetails, setPortfolioDetails] = useState(new Map());
  const [loading, setLoading] = useState(true);

  const apiBase = config?.apiBaseUrl || 'http://localhost:8000';

  const fetchAll = useCallback(async () => {
    if (!jwtToken || !config) return;
    setLoading(true);
    const headers = { Authorization: `Bearer ${jwtToken}` };

    try {
      // Phase 1: parallel metadata fetches
      const [userRes, agentsRes, portfoliosRes, invitesRes, observerRes] = await Promise.all([
        fetch(`${apiBase}/user`, { headers }),
        fetch(`${apiBase}/agents`, { headers }),
        fetch(`${apiBase}/portfolio`, { headers }),
        fetch(`${apiBase}/agents/invitations/pending`, { headers }),
        fetch(`${apiBase}/agents/observer-portfolios`, { headers }),
      ]);

      const user = userRes.ok ? await userRes.json() : null;
      setUserData(user);

      const agentsList = agentsRes.ok ? await agentsRes.json() : [];
      setAgents(agentsList);

      const portfolioList = portfoliosRes.ok ? await portfoliosRes.json() : [];
      // Store agent private keys in localStorage for Hyperliquid portfolios
      portfolioList.forEach(p => {
        if (p.agent_private_key && p.portfolio_type === 'HYPERLIQUID') {
          const key = `hyperliquid_agent_${p.is_testnet ? 'testnet' : 'mainnet'}_${p.id}`;
          localStorage.setItem(key, p.agent_private_key);
        }
      });
      setPortfolios(portfolioList);

      if (invitesRes.ok) setPendingInvites(await invitesRes.json());
      if (observerRes.ok) setObserverPortfolios(await observerRes.json());

      // Phase 2: parallel detail fetches for all portfolios
      if (portfolioList.length > 0) {
        const detailResults = await Promise.allSettled(
          portfolioList.map(p => {
            const fetchHeaders = { ...headers };
            if (p.portfolio_type === 'HYPERLIQUID') {
              const lsKey = `hyperliquid_agent_${p.is_testnet ? 'testnet' : 'mainnet'}_${p.id}`;
              const agentKey = localStorage.getItem(lsKey);
              if (agentKey) fetchHeaders['X-Agent-Private-Key'] = agentKey;
            }
            return fetch(`${apiBase}/portfolio/${p.id}`, { headers: fetchHeaders })
              .then(r => r.ok ? r.json() : null);
          })
        );

        const details = new Map();
        portfolioList.forEach((p, i) => {
          const result = detailResults[i];
          if (result.status === 'fulfilled' && result.value) {
            details.set(p.id, result.value);
          }
        });

        // Fetch timeseries for HL mainnet portfolio
        const hlP = portfolioList.find(p => p.portfolio_type === 'HYPERLIQUID' && !p.is_testnet);
        if (hlP) {
          const lsKey = `hyperliquid_agent_mainnet_${hlP.id}`;
          const agentKey = localStorage.getItem(lsKey);
          if (agentKey) {
            const tsRes = await fetch(
              `${apiBase}/portfolio/${hlP.id}/timeseries?granularity=1h&window=24H&limit=100`,
              { headers: { ...headers, 'X-Agent-Private-Key': agentKey } }
            ).then(r => r.ok ? r.json() : null).catch(() => null);
            if (tsRes?.timeseries) {
              const existing = details.get(hlP.id) || {};
              details.set(hlP.id, { ...existing, timeseries: tsRes.timeseries });
            }
          }
        }

        setPortfolioDetails(details);
      }
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [jwtToken, config, apiBase]);

  useEffect(() => {
    if (!jwtToken || !config) {
      setUserData(null);
      setAgents([]);
      setPortfolios([]);
      setObserverPortfolios([]);
      setPendingInvites([]);
      setPortfolioDetails(new Map());
      setLoading(false);
      return;
    }
    fetchAll();
  }, [jwtToken, config, fetchAll]);

  // Derived: partition portfolios
  const hlPortfolio = portfolios.find(p => p.portfolio_type === 'HYPERLIQUID' && !p.is_testnet) || null;
  const paperPortfolios = portfolios.filter(p => p !== hlPortfolio);

  // HL limits
  const hyperliquidMainnetCount = portfolios.filter(p => p.portfolio_type === 'HYPERLIQUID' && !p.is_testnet).length;
  const hyperliquidTestnetCount = portfolios.filter(p => p.portfolio_type === 'HYPERLIQUID' && p.is_testnet).length;

  return {
    userData,
    agents,
    portfolios,
    portfolioDetails,
    hlPortfolio,
    paperPortfolios,
    observerPortfolios,
    pendingInvites,
    setPendingInvites,
    hyperliquidMainnetCount,
    hyperliquidTestnetCount,
    loading,
    refetch: fetchAll,
  };
}
